type CaughtErrorReport = {
  error: unknown;
  fingerprint: Array<string>;
};

// React error-boundary catches are all reported from a single call site, so
// Sentry sees the same synthetic frame for every one of them and merges
// unrelated failures into one issue (`Error: undefined`, 94 events across five
// routes). Two problems, two fixes: report the innermost error that actually
// says something, and group on the failure rather than on the reporting frame.
export function getCaughtErrorReport(
  error: unknown,
  pathname: string,
): CaughtErrorReport {
  const reported = getReportableError(error);
  return {
    error: reported,
    fingerprint: [
      "react-caught",
      getErrorKey(reported),
      getRouteShape(pathname),
    ],
  };
}

const MAX_CAUSE_DEPTH = 5;

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

const DIGIT_RUN_PATTERN = /\d{2,}/g;

const USELESS_MESSAGES = new Set(["", "undefined", "null", "[object Object]"]);

// A wrapper whose message says nothing ("undefined") makes the Sentry title
// useless even though the real error sits one hop down `cause`. Report that one
// instead; Sentry keeps the chain visible either way.
function getReportableError(error: unknown): unknown {
  let current = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
    if (!(current instanceof Error) || hasUsefulMessage(current)) {
      return current;
    }
    const { cause } = current;
    if (!(cause instanceof Error)) {
      return current;
    }
    current = cause;
  }
  return current;
}

function hasUsefulMessage(error: Error): boolean {
  return !USELESS_MESSAGES.has(error.message.trim());
}

function getErrorKey(error: unknown): string {
  if (error instanceof Error) {
    const message = getStableText(error.message);
    return message ? `${error.name}: ${message}` : error.name;
  }
  if (error === null || error === undefined) {
    return `<${String(error)}>`;
  }
  return `<${typeof error}>`;
}

// Identifiers in the path or the message would split one bug into one issue per
// campaign, which is the opposite of the problem being fixed.
function getStableText(value: string): string {
  return value
    .replace(UUID_PATTERN, ":id")
    .replace(DIGIT_RUN_PATTERN, ":n")
    .trim();
}

function getRouteShape(pathname: string): string {
  const shape = getStableText(pathname);
  return shape === "" ? "/" : shape;
}
